import { combineEpics, ofType } from "redux-observable";
import { of, from, Observable, mergeMap, catchError } from "rxjs";
import { AnyAction } from "redux";
import { RootStateOrAny } from "react-redux";

import {
  fetchUserSuccess,
  changePassword as changePasswordAction,
  addUserSuccess,
  addUserFailure,
  fetchUserByIdSuccess,
  updateUserSuccess,
  updateUserFailed,
  searchUsersSuccess,
  searchUsersFailure,
  fetchTotalUsersSuccess,
  fetchTotalUsersFailure,
} from "./action";
import { succeededRequest, failedRequest } from "../request/action";
import { getCurrentUser } from "../auth/actions";

import { reauthenticateUserUsecase } from "../../core/usecases/interactors/auth/reauthenticateUser";
import { fetchUserUsecase } from "../../core/usecases/interactors/user/fetch-user";
import { addUserUseCase } from "../../core/usecases/interactors/user/add-user";
import { changePasswordUsecase } from "../../core/usecases/interactors/user/changePassword";
import { fetchUsersUsecase } from "../../core/usecases/interactors/user/fetch-users";
import { fetchTotalUsersUsecase } from "../../core/usecases/interactors/user/fetch-total-users";
import {
  FETCH_USER,
  CHANGE_PASSWORD,
  REAUTHENTICATE_CHANGE_PASSWORD,
  FETCH_USERS_REQUEST,
  ADD_USER_REQUEST,
  FETCH_USER_BY_ID,
  UPDATE_USER_REQUEST,
  SEARCH_USERS_REQUEST,
  UPDATE_CURRENT_USER_REQUEST,
  FETCH_TOTAL_USERS_REQUEST,
} from "./actionType";
import { LOGIN_SUCCESS } from "../auth/actionType";

import { errorString } from "../../utils/strings";
import { fetchUserByIdUsecase } from "../../core/usecases/interactors/user";
import { updateUserUsecase } from "../../core/usecases/interactors/user/updateUser";
import { data as constants } from "../../constants";
import { searchUsersUsecase } from "../../core/usecases/interactors/user/search-user";

export const fetchUserEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  { userInteractor }: { userInteractor: { fetchUser: fetchUserUsecase } }
): Observable<
  | ReturnType<typeof fetchUserSuccess>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(FETCH_USER, LOGIN_SUCCESS),
    mergeMap(({ type, payload: { id, isAdmin } }) => {
      return from(userInteractor.fetchUser(id, isAdmin)).pipe(
        mergeMap((response) => {
          return type === FETCH_USER
            ? of(succeededRequest(FETCH_USER, response))
            : of(getCurrentUser(response));
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(constants.commonNetworkError) &&
            window.location.reload();
          return of(failedRequest(type, errorMessage));
        })
      );
    })
  );

export const reaunthenticateChangePasswordEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    authInteractor: { reauthenticateUser },
  }: { authInteractor: { reauthenticateUser: reauthenticateUserUsecase } }
): Observable<
  | ReturnType<typeof fetchUserSuccess>
  | ReturnType<typeof changePasswordAction>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(REAUTHENTICATE_CHANGE_PASSWORD),
    mergeMap(({ payload }) => {
      return from(reauthenticateUser(payload.password)).pipe(
        mergeMap(() =>
          of(
            succeededRequest(REAUTHENTICATE_CHANGE_PASSWORD),
            changePasswordAction(payload.newPassword)
          )
        ),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(constants.commonNetworkError) &&
            window.location.reload();
          return of(
            failedRequest(REAUTHENTICATE_CHANGE_PASSWORD, errorMessage)
          );
        })
      );
    })
  );

export const changePasswordEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    userInteractor: { changePassword },
  }: { userInteractor: { changePassword: changePasswordUsecase } }
): Observable<
  | ReturnType<typeof fetchUserSuccess>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(CHANGE_PASSWORD),
    mergeMap(({ payload }) => {
      return from(changePassword(payload.newPassword)).pipe(
        mergeMap(() => of(succeededRequest(CHANGE_PASSWORD))),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(constants.commonNetworkError) &&
            window.location.reload();
          return of(failedRequest(CHANGE_PASSWORD, errorMessage));
        })
      );
    })
  );

export const fetchUserByIdEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    userInteractor,
  }: { userInteractor: { fetchUserById: fetchUserByIdUsecase } }
): Observable<
  | ReturnType<typeof fetchUserSuccess>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(FETCH_USER_BY_ID),
    mergeMap(({ type, payload: { id } }) => {
      return from(userInteractor.fetchUserById(id)).pipe(
        mergeMap((response) => {
          return of(
            fetchUserByIdSuccess(response),
            succeededRequest(FETCH_USER_BY_ID, response)
          );
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(
            "Failed to get document because the client is offline."
          ) && window.location.reload();
          return of(failedRequest(type, errorMessage));
        })
      );
    })
  );

export const fetchUsersEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    userInteractor: { fetchUsers },
  }: { userInteractor: { fetchUsers: fetchUsersUsecase } }
) =>
  action$.pipe(
    ofType(FETCH_USERS_REQUEST),
    mergeMap(({ payload: { query } }) => {
      return from(fetchUsers(query)).pipe(
        mergeMap((response) => of(succeededRequest(FETCH_USERS_REQUEST, response))),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(constants.commonNetworkError) &&
            window.location.reload();
          return of(failedRequest(FETCH_USERS_REQUEST, errorMessage));
        })
      );
    })
  );

export const addUserEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    userInteractor: { addUser },
  }: { userInteractor: { addUser: addUserUseCase } }
): Observable<
  | ReturnType<typeof addUserSuccess>
  | ReturnType<typeof addUserFailure>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(ADD_USER_REQUEST),
    mergeMap(({ payload: { user } }) => {
      return from(addUser(user)).pipe(
        mergeMap(() => {
          return of(addUserSuccess(), succeededRequest(ADD_USER_REQUEST, user));
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage.includes(constants.commonNetworkError)) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          return of(
            addUserFailure(errorMessage),
            failedRequest(ADD_USER_REQUEST, errorMessage)
          );
        })
      );
    })
  );

export const updateUserEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  { userInteractor }: { userInteractor: { updateUser: updateUserUsecase } }
): Observable<
  | ReturnType<typeof updateUserSuccess>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
  | ReturnType<typeof updateUserFailed>
> =>
  action$.pipe(
    ofType(UPDATE_USER_REQUEST, UPDATE_CURRENT_USER_REQUEST),
    mergeMap(({ type, payload: { id, data } }) => {
      return from(userInteractor.updateUser(id, data)).pipe(
        mergeMap(() => {
          return type === UPDATE_USER_REQUEST ? of(
            succeededRequest(UPDATE_USER_REQUEST, data),
            updateUserSuccess()
          ) : (of(succeededRequest(UPDATE_CURRENT_USER_REQUEST, data)));
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage.includes(constants.commonNetworkError)) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          errorMessage.includes("Network Error") && window.location.reload();
          return of(failedRequest(type, errorMessage), updateUserFailed());
        })
      );
    })
  );

export const searchUsersEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    userInteractor: { searchUsers },
  }: { userInteractor: { searchUsers: searchUsersUsecase } }
) =>
  action$.pipe(
    ofType(SEARCH_USERS_REQUEST),
    mergeMap(({ payload: { keyword } }) => {
      return from(searchUsers(keyword)).pipe(
        mergeMap((response) => of(searchUsersSuccess(response))),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage.includes(constants.commonNetworkError) &&
            window.location.reload();
          return of(searchUsersFailure(errorMessage));
        })
      );
    })
  );

export const fetchTotalUsersEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    userInteractor: { fetchTotalUsers },
  }: {
    userInteractor: {
      fetchTotalUsers: fetchTotalUsersUsecase;
    };
  }
): Observable<
  | ReturnType<typeof fetchTotalUsersSuccess>
  | ReturnType<typeof fetchTotalUsersFailure>
> =>
  action$.pipe(
    ofType(FETCH_TOTAL_USERS_REQUEST),
    mergeMap(() => {
      return from(fetchTotalUsers()).pipe(
        mergeMap((response) => {
          return of(fetchTotalUsersSuccess(response));
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage === constants.commonNetworkError &&
            window.location.reload();
          return of(fetchTotalUsersFailure(errorMessage));
        })
      );
    })
  );

export default combineEpics(
  fetchUserEpic,
  // @ts-ignore
  reaunthenticateChangePasswordEpic,
  changePasswordEpic,
  addUserEpic,
  fetchUsersEpic,
  fetchUserByIdEpic,
  updateUserEpic,
  searchUsersEpic,
  fetchTotalUsersEpic,
);
